import { ComputedRef, MaybeRefOrGetter, computed, toValue } from 'vue'
import { useRouter } from 'vue-router'

import { useConfirm } from 'primevue/useconfirm'

import {
  useEmployeeShiftOfferRemoveMutation,
  useEmployeeShiftOfferUpdateMutation,
} from '../../employee_shift/composables/useEmployeeShiftOffer'
import { useSiteRouter } from '../../sites/composables/useSiteRouter'

import { EmployeeShiftOffer } from '../../employee_shift/models/employeeShiftOffer'
import { Employee } from '../../employees/models/employee'
import { Shift } from '../../shifts/models/shift'
import { SiteID } from '../../sites/models/site'

import {
  ShiftOfferActionDefinition,
  ShiftOfferActionType,
  shiftOfferActionDefinitions,
} from '../types/offerAction.types'

export interface UseOfferActionsOptions {
  siteId: MaybeRefOrGetter<SiteID>
  shift: MaybeRefOrGetter<Shift>
  employee: MaybeRefOrGetter<Employee>
  offer: MaybeRefOrGetter<EmployeeShiftOffer>
}

export interface UseOfferActionsReturn {
  mainActions: ComputedRef<ShiftOfferActionDefinition[]>
  otherActions: ComputedRef<ShiftOfferActionDefinition[]>
  executeAction: (type: ShiftOfferActionType) => void
  isActionLoading: (type: ShiftOfferActionType) => boolean
}

export function useOfferActions(
  options: UseOfferActionsOptions,
): UseOfferActionsReturn {
  const { siteId, shift, employee, offer } = options

  const router = useRouter()
  const confirm = useConfirm()
  const { routes } = useSiteRouter()

  const { mutate: updateOffer, isPending: isUpdateOfferPending } =
    useEmployeeShiftOfferUpdateMutation()

  const { mutate: removeOffer, isPending: isRemoveOfferPending } =
    useEmployeeShiftOfferRemoveMutation()

  const isActionAvailable = (action: ShiftOfferActionDefinition): boolean => {
    if (
      (action.requiredStatus &&
        !action.requiredStatus.includes(toValue(offer).status)) ||
      (action.isAvailable &&
        !action.isAvailable({
          shift: toValue(shift),
          employee: toValue(employee),
          offer: toValue(offer),
        }))
    ) {
      return false
    }

    return true
  }

  const availableActions = computed(() => {
    return shiftOfferActionDefinitions
      .filter((action) => {
        return isActionAvailable(action)
      })
      .sort((a, b) => a.order - b.order)
  })

  const mainActions = computed(() => {
    return availableActions.value.filter((action) => action.isMain)
  })

  const otherActions = computed(() => {
    return availableActions.value.filter((action) => !action.isMain)
  })

  const executeAction = (type: ShiftOfferActionType) => {
    const action = shiftOfferActionDefinitions.find((a) => a.type === type)
    if (!action) {
      throw new Error(`Action ${type} not found`)
    }

    if (!isActionAvailable(action)) {
      throw new Error(
        `Action ${type} is not available for the current offer status`,
      )
    }

    if (action.requiresConfirmation) {
      const confirmationMessage =
        action.confirmationMessage ??
        'Are you sure you want to perform this action?'
      return new Promise<void>((resolve, reject) => {
        confirm.require({
          message:
            typeof confirmationMessage === 'function'
              ? confirmationMessage({
                  shift: toValue(shift),
                  employee: toValue(employee),
                  offer: toValue(offer),
                })
              : confirmationMessage,
          header: 'Confirmation',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Remove',
          acceptProps: { severity: 'danger' },
          accept: () => {
            performAction(action)
            resolve()
          },
          rejectLabel: 'Cancel',
          rejectProps: { severity: 'secondary' },
          reject: () => {
            reject(new Error('Action cancelled by user'))
          },
        })
      })
    } else {
      performAction(action)
    }
  }

  const performAction = (action: ShiftOfferActionDefinition) => {
    if (action.setStatus) {
      updateOffer({
        siteId: toValue(siteId),
        shiftId: toValue(shift).id,
        employeeId: toValue(employee).id,
        status: action.setStatus,
      })

      return
    } else if (action.type === 'remove') {
      removeOffer({
        siteId: toValue(siteId),
        shiftId: toValue(shift).id,
        employeeId: toValue(employee).id,
      })

      return
    } else if (action.type === 'view_employee') {
      router.push(routes.employeeDetail(toValue(employee).id))
    } else {
      console.warn(`No mutation defined for action type: ${action.type}`)

      action.handler({
        shift: toValue(shift),
        employee: toValue(employee),
        offer: toValue(offer),
      })
    }
  }

  const isActionLoading = (type: ShiftOfferActionType): boolean => {
    if (type === 'remove') {
      return isRemoveOfferPending.value
    }

    if (['approve', 'assign', 'cancel', 'reject'].includes(type)) {
      return isUpdateOfferPending.value
    }

    return false
  }

  return {
    mainActions,
    otherActions,
    executeAction,
    isActionLoading,
  }
}
